import React from 'react';
import { useForm } from 'react-hook-form';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';
import { FaSearch } from 'react-icons/fa';
import TsdiClient from 'src/models/AP/Client';
import ICostBreakdownProspectCandidate from '../models/costBreakdownProspectCandidate';

interface IAddCostBreakdownReportDialogProps {
    dialogName: string;
    client?: TsdiClient;
    initialClientNumber?: number;
    initialBegDate: string;
    initialEndDate: string;
    onClientNumberBlur: (clientNumber: string) => void;
    onFindClient: () => void;
    prospects: Array<ICostBreakdownProspectCandidate>;
    selected: Record<number, boolean>;
    onToggleProspect: (prId: number) => void;
    onSelectAll: () => void;
    onDeselectAll: () => void;
    onCancel: () => void;
    onCreate: (begDate: string, endDate: string, includeAdmin: boolean) => void;
}

// Mirrors the Prospects screen (Client No./Find Client/Beg-End Date/Prospect grid with Select
// All/Deselect All/Add Report). Oracle's two separate buttons ("Add Report - No Admin"/"Add
// Report - W/Admin", differing only in the INCLUDE_ADMIN value they insert) collapsed here into
// one button plus an "Include Admin" checkbox, per explicit user direction - two buttons whose only
// difference is a boolean flag is exactly what a checkbox is for. Beg/End Date default in from the
// search panel per the user's own direction rather than a separate Dates popup, but stay editable
// here (uncontrolled react-hook-form fields, read at Create time - the prospects list/selection are
// the only pieces that need to survive this dialog's own unmount when Find Client interrupts it,
// so those alone are lifted to the parent page).
const AddCostBreakdownReportDialog = (props: IAddCostBreakdownReportDialogProps) => {
    const { register, getValues } = useForm();
    const selectedCount = Object.values(props.selected).filter(Boolean).length;

    const handleCreate = () => {
        const values = getValues();
        props.onCreate(values.begDate, values.endDate, !!values.includeAdmin);
    };

    return (
        <Dialog title="Add Cost Breakdown Report" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '40%' }}>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '.5rem' }}>
                <div style={{ width: '8rem', marginRight: '.25rem' }}>
                    <RHFInputBox
                        label="Client No."
                        labelStyle={{ whiteSpace: 'nowrap' }}
                        name="clientNumber"
                        type="number"
                        register={register}
                        defaultVal={props.initialClientNumber ? String(props.initialClientNumber) : ''}
                        onBlur={(e) => props.onClientNumberBlur(e.currentTarget.value)}
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                </div>
                <Button
                    reactIcon={FaSearch}
                    iconStyle={{ width: '1.5rem', height: '1.5rem' }}
                    iconHoverColor="blue"
                    title="Find Client"
                    style={{ height: '3rem', marginRight: '1rem', background: 'transparent' }}
                    text=""
                    onClick={() => props.onFindClient()}
                />
                <div style={{ marginRight: '1rem', minWidth: '12rem' }}>
                    {props.client && <div style={{ fontWeight: 'bold' }}>{props.client.clientNumber} - {props.client.clientName}</div>}
                </div>
                <RHFInputBox
                    label="Beg. Date"
                    labelStyle={{ whiteSpace: 'nowrap' }}
                    name="begDate"
                    type="date"
                    register={register}
                    defaultVal={props.initialBegDate}
                    style={{ width: '100%' }}
                    bodyStyle={{ width: '10rem', marginRight: '.5rem' }}
                />
                <RHFInputBox
                    label="End Date"
                    labelStyle={{ whiteSpace: 'nowrap' }}
                    name="endDate"
                    type="date"
                    register={register}
                    defaultVal={props.initialEndDate}
                    style={{ width: '100%' }}
                    bodyStyle={{ width: '10rem' }}
                />
                <RHFCheckbox
                    label="Include Admin"
                    labelStyle={{ whiteSpace: 'nowrap' }}
                    name="includeAdmin"
                    register={register}
                    defaultChecked={false}
                    bodyStyle={{ width: 'auto', marginLeft: 'auto' }}
                />
            </div>
            <div style={{ overflowY: 'scroll', maxHeight: '20rem', border: '1px solid #ccc' }}>
                <table style={{ width: '100%' }}>
                    <thead>
                        <tr>
                            <th style={{ width: '10%' }} />
                            <th style={{ width: '20%' }}>No.</th>
                            <th style={{ textAlign: 'left' }}>Name</th>
                        </tr>
                    </thead>
                    <tbody>
                        {props.prospects.length === 0 && (
                            <tr>
                                <td colSpan={3} className="bold-centered">Enter or find a Client No. to list its projects.</td>
                            </tr>
                        )}
                        {props.prospects.map((prospect) => (
                            <tr
                                key={prospect.prId}
                                className="table-item-hover"
                                style={{ cursor: 'pointer' }}
                                onClick={() => props.onToggleProspect(prospect.prId)}
                            >
                                <td style={{ textAlign: 'center' }}>
                                    <input type="checkbox" checked={!!props.selected[prospect.prId]} readOnly={true} />
                                </td>
                                <td style={{ textAlign: 'center' }}>{prospect.prNo}</td>
                                <td>{prospect.prName}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="form-row" style={{ marginTop: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button className="blue-hover-button" style={{ marginRight: '1rem', marginBottom: '.5rem' }} text="Select All" onClick={() => props.onSelectAll()} />
                <Button className="blue-hover-button" style={{ marginRight: '1rem', marginBottom: '.5rem' }} text="Deselect All" onClick={() => props.onDeselectAll()} />
                <Button className="blue-hover-button" style={{ marginRight: '1rem', marginBottom: '.5rem' }} text="Cancel" onClick={() => props.onCancel()} />
                <Button className="blue-hover-button" style={{ marginBottom: '.5rem' }} text="Add Report" onClick={() => handleCreate()} />
            </div>
            <div style={{ textAlign: 'center', color: '#666' }}>{selectedCount} of {props.prospects.length} project(s) selected</div>
        </Dialog>
    );
};

export default AddCostBreakdownReportDialog;



// WEBPACK FOOTER //
// ./src/components/project-billing/dialogs/AddCostBreakdownReportDialog.tsx